// The console's support, staff and audit calls (spec 15 §5; M15-8 and M15-9),
// through the one generated client like the rest of the area (doctrine 3).
// Every mutation carries an Idempotency-Key the caller holds (lib/held-key.ts),
// so a retry after a lost answer replays rather than acts twice.

import { client } from "../../../lib/api";
import type { components } from "@assessly/api-client";
import { PlatformError, idempotent, isProblem, unwrap } from "./platform-api";

export type TenantUser = components["schemas"]["PlatformTenantUser"];
export type Grant = components["schemas"]["ImpersonationGrantListItem"];
export type GrantPage = components["schemas"]["ImpersonationListResponse"];
export type GrantView = components["schemas"]["ImpersonationGrantView"];
export type CreateGrantInput = components["schemas"]["ImpersonationCreateRequest"];
export type CreateGrantResult = components["schemas"]["ImpersonationCreateResponse"];
export type StaffMember = components["schemas"]["PlatformStaffView"];
export type StaffRole = StaffMember["role"];
export type StaffInviteInput = components["schemas"]["PlatformStaffInviteRequest"];
export type StaffPatchInput = components["schemas"]["PlatformStaffPatchRequest"];
export type AuditRow = components["schemas"]["PlatformAuditRow"];
export type AuditPage = components["schemas"]["PlatformAuditLogResponse"];
export type ChainVerification = components["schemas"]["PlatformChainVerification"];

// ---- S10: the tenant's people and the support actions (§3.8) --------------

export async function listTenantUsers(
  tenantId: string,
  q: string,
  signal?: AbortSignal,
): Promise<readonly TenantUser[]> {
  const result = await client.GET("/v1/platform/tenants/{id}/users", {
    params: { path: { id: tenantId }, query: q === "" ? { limit: 50 } : { q, limit: 50 } },
    ...(signal ? { signal } : {}),
  });
  return unwrap(result, "could not load the tenant's people").items;
}

export async function sendUserPasswordReset(
  tenantId: string,
  userId: string,
  key: string,
): Promise<void> {
  const result = await client.POST("/v1/platform/tenants/{id}/users/{userId}/password-reset", {
    params: { path: { id: tenantId, userId } },
    headers: idempotent(key),
  });
  unwrap(result, "could not send the reset link");
}

export async function revokeUserSessions(
  tenantId: string,
  userId: string,
  key: string,
): Promise<number> {
  const result = await client.POST("/v1/platform/tenants/{id}/users/{userId}/revoke-sessions", {
    params: { path: { id: tenantId, userId } },
    headers: idempotent(key),
  });
  return unwrap(result, "could not end the sessions").sessions_revoked;
}

export async function revokeTenantSessions(
  tenantId: string,
  typedConfirmation: string,
  key: string,
): Promise<{ sessions_revoked: number; grants_revoked: number }> {
  const result = await client.POST("/v1/platform/tenants/{id}/revoke-all-sessions", {
    params: { path: { id: tenantId } },
    headers: idempotent(key),
    body: { typed_confirmation: typedConfirmation },
  });
  return unwrap(result, "could not end the tenant's sessions");
}

// ---- impersonation grants (§3.7) -------------------------------------------

export async function listGrants(
  query: { tenant_id?: string; status?: string; cursor?: string; limit?: number },
  signal?: AbortSignal,
): Promise<GrantPage> {
  const result = await client.GET("/v1/platform/impersonations", {
    params: { query },
    ...(signal ? { signal } : {}),
  });
  return unwrap(result, "could not load the grants");
}

export async function createGrant(
  input: CreateGrantInput,
  key: string,
): Promise<CreateGrantResult> {
  const result = await client.POST("/v1/platform/impersonations", {
    body: input,
    headers: idempotent(key),
  });
  return unwrap(result, "could not request the grant");
}

export async function approveGrant(id: string, key: string): Promise<GrantView> {
  const result = await client.POST("/v1/platform/impersonations/{id}/approve", {
    params: { path: { id } },
    headers: idempotent(key),
  });
  return unwrap(result, "could not approve the grant");
}

export async function mintGrantToken(id: string, key: string): Promise<string> {
  const result = await client.POST("/v1/platform/impersonations/{id}/token", {
    params: { path: { id } },
    headers: idempotent(key),
  });
  return unwrap(result, "could not open the tenant").redirect_url;
}

export async function endGrant(id: string, key: string): Promise<GrantView> {
  const result = await client.POST("/v1/platform/impersonations/{id}/end", {
    params: { path: { id } },
    headers: idempotent(key),
  });
  return unwrap(result, "could not end the grant");
}

// ---- S17: staff management (§3.1 step 5) -----------------------------------

export async function inviteStaff(input: StaffInviteInput, key: string): Promise<StaffMember> {
  const result = await client.POST("/v1/platform/staff", {
    body: input,
    headers: idempotent(key),
  });
  return unwrap(result, "could not invite the staff member");
}

export async function updateStaff(
  id: string,
  input: StaffPatchInput,
  key: string,
): Promise<StaffMember> {
  const result = await client.PATCH("/v1/platform/staff/{id}", {
    params: { path: { id } },
    headers: idempotent(key),
    body: input,
  });
  return unwrap(result, "could not change the staff member");
}

export async function resendStaffInvite(id: string, key: string): Promise<StaffMember> {
  const result = await client.POST("/v1/platform/staff/{id}/resend-invite", {
    params: { path: { id } },
    headers: idempotent(key),
  });
  return unwrap(result, "could not re-send the invite");
}

export async function resetStaffTotp(
  id: string,
  reason: string,
  key: string,
): Promise<StaffMember> {
  const result = await client.POST("/v1/platform/staff/{id}/totp-reset", {
    params: { path: { id } },
    headers: idempotent(key),
    body: { reason },
  });
  return unwrap(result, "could not reset the second factor");
}

// ---- S18: the platform audit log -------------------------------------------

export interface AuditFilters {
  staff_id?: string;
  action_prefix?: string;
  tenant_id?: string;
  from?: string;
  to?: string;
}

export async function listAuditLog(
  filters: AuditFilters,
  cursor: string | undefined,
  signal?: AbortSignal,
): Promise<AuditPage> {
  const result = await client.GET("/v1/platform/audit-log", {
    params: { query: { ...filters, limit: 50, ...(cursor !== undefined ? { cursor } : {}) } },
    ...(signal ? { signal } : {}),
  });
  const page = unwrap(result, "could not load the audit log");
  // The route also answers NDJSON, which this read never asks for.
  if (typeof page === "string") {
    throw new PlatformError(result.response.status, undefined, "unexpected NDJSON answer");
  }
  return page;
}

/** The whole filtered stream as NDJSON text (§5's Accept export). */
export async function exportAuditLog(filters: AuditFilters): Promise<string> {
  const result = await client.GET("/v1/platform/audit-log", {
    params: { query: filters },
    headers: { Accept: "application/x-ndjson" },
    parseAs: "text",
  });
  if (result.error !== undefined || !result.response.ok || typeof result.data !== "string") {
    throw new PlatformError(
      result.response.status,
      isProblem(result.error) ? result.error : undefined,
      "could not export the audit log",
    );
  }
  return result.data;
}

export async function getChainVerification(signal?: AbortSignal): Promise<ChainVerification> {
  const result = await client.GET("/v1/platform/audit-log/verification", signal ? { signal } : {});
  return unwrap(result, "could not read the chain's verification");
}

export async function verifyChainNow(key: string): Promise<ChainVerification> {
  const result = await client.POST("/v1/platform/audit-log/verification", {
    headers: idempotent(key),
  });
  return unwrap(result, "could not verify the chain");
}
